import {
  Box,
  Typography,
  Switch,
  IconButton,
  TextField,
  Autocomplete,
  Chip,
  InputAdornment,
  Stack,
  Tooltip,
} from '@mui/material';
import dayjs from 'dayjs';
import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios, { endpoints } from 'src/utils/axios';
import { useGetBusinessUnitsForSelection } from 'src/actions/business-unit';
import { Iconify } from 'src/components/iconify';
import { RestrictedDatePicker } from 'src/components/restricted-date-picker';
import { StyledButton } from 'src/components/visit-allocation-modal/styled';
import { ActivityDTO, ActivityType } from 'src/types/dto';
import { getCompetencyName } from 'src/utils/competency';
import {
  formatNumberToMaxPlaces,
  getMaxDecimalPlacesPattern,
  isNonNegativeDecimalWithMaxPlaces,
  normalizeDecimalInputToMaxPlaces,
  validatePositiveDecimalMaxPlacesInput,
} from 'src/utils/decimal-places';
import { roundDuration } from 'src/utils/duration';
import {
  getActivityDurationDays,
  getDefaultAllocationDurationDays,
  mergeAllocationTabFiltersState,
  type AllocationTabFiltersState,
} from './allocation-duration';

export {
  buildInspectorSelectionFilterSnapshot,
  getActivityDurationDays,
  getAlreadyAllocatedDaysForAllocationBudget,
  getDefaultAllocationDurationDays,
  getDefaultRemainingAllocationDurationDays,
  getDurationDaysAfterInspectorDeselect,
  getDurationDaysForInspectorSelection,
  getExistingAllocatedDurationDays,
  getRemainingAllocationDurationDays,
  getTotalSelectedDurationDays,
  hasInvalidCountingDurations,
  hasInvalidNonCountingDurations,
  isDurationRowEditable,
  mergeAllocationTabFiltersState,
  MAX_NON_COUNTING_ALLOCATION_DAYS,
  preserveOrRedistributeSelectedDurations,
  redistributeSelectedInspectorDurations,
  resolveDurationInputBlur,
  type AllocationTabFiltersState,
  type InspectorSelectionFilterSnapshot,
  type SelectedInspectorEntry,
} from './allocation-duration';

const MAX_DURATION_DECIMAL_PLACES = 5;
const MAX_DISTANCE_KM_DECIMAL_PLACES = 1;

/** Match compact numeric filters (Duration, max distance) — fixed width so labels do not stretch the row. */
const COMPACT_NUMERIC_FILTER_FIELD_SX = {
  width: 140,
  minWidth: 140,
  flexShrink: 0,
} as const;

interface AllocationTabFiltersProps {
  breakInChangesEnabled: boolean;
  onBreakInChangesChange: (value: boolean) => void;
  onBreakInChangesSubmit?: (solutionId: number) => void;
  activity: ActivityDTO;
  filters: AllocationTabFiltersState;
  onFiltersChange: (filters: AllocationTabFiltersState) => void;
  showBreakInChangesToggle?: boolean;
  /** Remaining activity duration available for the next selection (defaults to full activity duration). */
  maxDurationDays?: number;
}
export function AllocationTabFilters({
  filters,
  breakInChangesEnabled,
  onBreakInChangesChange,
  onBreakInChangesSubmit,
  activity,
  onFiltersChange,
  showBreakInChangesToggle = true,
  maxDurationDays: maxDurationDaysProp,
}: AllocationTabFiltersProps) {
  const { t } = useTranslation(['activities', 'common']);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isOnlyInternal, setIsOnlyInternal] = useState<boolean>(filters?.isOnlyInternal ?? false);
  const [isOnlyPreferred, setIsOnlyPreferred] = useState<boolean>(
    filters?.isOnlyPreferred ?? true
  );
  const [isRecommended, setIsRecommended] = useState<boolean>(filters?.isRecommended ?? true);
  const [searchText, setSearchText] = useState<string>(filters?.searchText ?? '');
  const maxDurationDays = useMemo(() => {
    const cap =
      maxDurationDaysProp != null && Number.isFinite(maxDurationDaysProp)
        ? maxDurationDaysProp
        : getActivityDurationDays(activity);
    return roundDuration(Math.max(0, cap));
  }, [activity, maxDurationDaysProp]);
  const defaultDurationDays = useMemo(
    () => formatNumberToMaxPlaces(maxDurationDays, MAX_DURATION_DECIMAL_PLACES),
    [maxDurationDays]
  );
  const [durationDays, setDurationDays] = useState<string>(
    filters?.durationDays ?? defaultDurationDays
  );
  const [durationError, setDurationError] = useState<string | null>(null);
  const [maxDistanceKm, setMaxDistanceKm] = useState<string>(filters?.maxDistanceKm ?? '');
  const [maxDistanceError, setMaxDistanceError] = useState<string | null>(null);
  const isDeskService = activity?.type === ActivityType.DeskService;
  const preferredDayButtonRef = useRef<HTMLButtonElement>(null);
  const preferredDayRef = useRef(filters.preferredDay);
  preferredDayRef.current = filters.preferredDay;
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dependencyDefaultAppliedForActivityRef = useRef<number | null>(null);
  const [dependencyCanSchedule, setDependencyCanSchedule] = useState(true);
  const [dependencyEarliestStart, setDependencyEarliestStart] = useState<dayjs.Dayjs | null>(null);
  const [dependencyLatestStart, setDependencyLatestStart] = useState<dayjs.Dayjs | null>(null);

  useEffect(() => {
    dependencyDefaultAppliedForActivityRef.current = null;
  }, [activity?.id]);

  useEffect(() => {
    if (!activity?.id) {
      setDependencyCanSchedule(true);
      setDependencyEarliestStart(null);
      setDependencyLatestStart(null);
      return;
    }

    setDependencyCanSchedule(true);
    setDependencyEarliestStart(null);
    setDependencyLatestStart(null);

    let cancelled = false;
    axios
      .get<{
        canSchedule: boolean;
        dependencyEarliestStart?: string;
        dependencyLatestStart?: string;
      }>(endpoints.activities.dependencySchedulingConstraints(activity.id))
      .then((res) => {
        if (cancelled) return;
        const earliest = res.data?.dependencyEarliestStart;
        const latest = res.data?.dependencyLatestStart;
        setDependencyCanSchedule(res.data?.canSchedule ?? true);
        setDependencyEarliestStart(earliest ? dayjs(earliest) : null);
        setDependencyLatestStart(latest ? dayjs(latest) : null);
      })
      .catch(() => {
        if (!cancelled) {
          setDependencyCanSchedule(true);
          setDependencyEarliestStart(null);
          setDependencyLatestStart(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activity?.id]);

  const { businessUnitOptions, businessUnitOptionsLoading, mutateBusinessUnitOptions } =
    useGetBusinessUnitsForSelection();

  useEffect(() => {
    mutateBusinessUnitOptions();
  }, [activity?.id, mutateBusinessUnitOptions]);

  const businessUnitFilterOptions = useMemo(
    () => businessUnitOptions.map((bu) => ({ key: bu.id, label: bu.name })),
    [businessUnitOptions]
  );

  const competencyOptions = useMemo(() => {
    return (
      activity?.activityCompetencies?.map((competency) => ({
        key: competency.competency.id,
        label: getCompetencyName(competency.competency),
      })) || []
    );
  }, [activity]);

  const [selectedCompetencies, setSelectedCompetencies] = useState<
    { key: number; label: string }[]
  >(
    competencyOptions?.filter((competency) =>
      filters?.selectedCompetencyIds?.includes(competency.key)
    ) ?? []
  );

  const [selectedBusinessUnits, setSelectedBusinessUnits] = useState<
    { key: number; label: string }[]
  >(
    businessUnitFilterOptions?.filter((bu) => filters?.selectedBusinessUnitIds?.includes(bu.key)) ??
      []
  );

  useEffect(() => {
    if (filters?.isOnlyInternal !== isOnlyInternal) {
      setIsOnlyInternal(filters?.isOnlyInternal ?? false);
    }
    if (filters?.isOnlyPreferred !== isOnlyPreferred) {
      setIsOnlyPreferred(filters?.isOnlyPreferred ?? true);
    }
    if (filters?.isRecommended !== isRecommended) {
      setIsRecommended(filters?.isRecommended ?? true);
    }
    if (
      filters?.selectedCompetencyIds !== selectedCompetencies?.map((competency) => competency.key)
    ) {
      setSelectedCompetencies(
        competencyOptions?.filter((competency) =>
          filters?.selectedCompetencyIds?.includes(competency.key)
        ) ?? []
      );
    }
    if (filters?.searchText !== searchText) {
      setSearchText(filters?.searchText ?? '');
    }
    if (
      JSON.stringify(filters?.selectedBusinessUnitIds ?? []) !==
      JSON.stringify(selectedBusinessUnits?.map((bu) => bu.key) ?? [])
    ) {
      setSelectedBusinessUnits(
        businessUnitFilterOptions?.filter((bu) =>
          filters?.selectedBusinessUnitIds?.includes(bu.key)
        ) ?? []
      );
    }
  }, [filters, competencyOptions, businessUnitFilterOptions]);

  useEffect(() => {
    setDurationDays(filters?.durationDays ?? defaultDurationDays);
  }, [filters?.durationDays, defaultDurationDays]);

  useEffect(() => {
    setMaxDistanceKm(filters?.maxDistanceKm ?? '');
  }, [filters?.maxDistanceKm]);

  const emitFilters = useCallback(
    (patch: Partial<AllocationTabFiltersState>) => {
      onFiltersChange(
        mergeAllocationTabFiltersState(
          { preferredDay: preferredDayRef.current },
          {
            isOnlyInternal,
            isOnlyPreferred,
            isRecommended,
            selectedCompetencyIds: selectedCompetencies?.map((c) => c.key) ?? [],
            selectedBusinessUnitIds: selectedBusinessUnits?.map((bu) => bu.key) ?? [],
            searchText,
            durationDays,
            maxDistanceKm,
          },
          patch
        )
      );
    },
    [
      isOnlyInternal,
      isOnlyPreferred,
      isRecommended,
      selectedCompetencies,
      selectedBusinessUnits,
      searchText,
      durationDays,
      maxDistanceKm,
      onFiltersChange,
    ]
  );

  useEffect(() => {
    if (
      !activity?.id ||
      !dependencyCanSchedule ||
      !dependencyEarliestStart?.isValid() ||
      filters.preferredDay != null ||
      dependencyDefaultAppliedForActivityRef.current === activity.id
    ) {
      return;
    }

    dependencyDefaultAppliedForActivityRef.current = activity.id;
    const defaultDay = dependencyEarliestStart.clone().startOf('day');
    emitFilters({ preferredDay: defaultDay });
  }, [
    activity?.id,
    dependencyCanSchedule,
    dependencyEarliestStart,
    filters.preferredDay,
    emitFilters,
  ]);

  const emitDurationDaysFilter = useCallback(
    (value: string) => {
      emitFilters({ durationDays: value });
    },
    [emitFilters]
  );

  useEffect(() => {
    const current = Number(durationDays);
    if (!Number.isFinite(current) || current <= maxDurationDays) {
      return;
    }
    const clamped = formatNumberToMaxPlaces(maxDurationDays, MAX_DURATION_DECIMAL_PLACES);
    setDurationDays(clamped);
    setDurationError(null);
    emitDurationDaysFilter(clamped);
  }, [maxDurationDays, durationDays, emitDurationDaysFilter]);

  const commitDurationDays = useCallback(
    (raw: string) => {
      const apply = (next: string, error: string | null) => {
        setDurationDays(next);
        setDurationError(error);
        // Skip re-emitting an unchanged value — parent filter updates remount the
        // inspector list and swallow the click that caused this blur.
        if (next !== filters.durationDays) {
          emitDurationDaysFilter(next);
        }
      };

      if (!isNonNegativeDecimalWithMaxPlaces(raw, MAX_DURATION_DECIMAL_PLACES)) {
        apply(defaultDurationDays, null);
        return;
      }

      const num = Number(raw);
      if (num <= 0) {
        apply(defaultDurationDays, null);
        return;
      }
      if (num > maxDurationDays) {
        apply(
          defaultDurationDays,
          t('allocation.details.filters.durationDays.exceedsMax', {
            max: defaultDurationDays,
          })
        );
        return;
      }

      apply(formatNumberToMaxPlaces(num, MAX_DURATION_DECIMAL_PLACES), null);
    },
    [defaultDurationDays, emitDurationDaysFilter, filters.durationDays, maxDurationDays, t]
  );

  const onDurationChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = normalizeDecimalInputToMaxPlaces(e.target.value, MAX_DURATION_DECIMAL_PLACES);
      setDurationDays(raw);
      setDurationError(null);

      if (raw === '' || !isNonNegativeDecimalWithMaxPlaces(raw, MAX_DURATION_DECIMAL_PLACES)) {
        return;
      }

      const num = Number(raw);
      if (num <= 0) {
        return;
      }
      if (num > maxDurationDays) {
        setDurationError(
          t('allocation.details.filters.durationDays.exceedsMax', {
            max: defaultDurationDays,
          })
        );
        return;
      }

      const normalized = formatNumberToMaxPlaces(num, MAX_DURATION_DECIMAL_PLACES);
      if (normalized !== filters.durationDays) {
        emitDurationDaysFilter(normalized);
      }
    },
    [defaultDurationDays, emitDurationDaysFilter, filters.durationDays, maxDurationDays, t]
  );

  const onDurationBlur = useCallback(() => {
    commitDurationDays(durationDays);
  }, [commitDurationDays, durationDays]);

  const onDurationKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
    validatePositiveDecimalMaxPlacesInput(e, MAX_DURATION_DECIMAL_PLACES);
    if (e.key !== 'Enter') {
      return;
    }
    e.preventDefault();
    (e.target as HTMLInputElement).blur();
  }, []);

  const emitMaxDistanceKmFilter = useCallback(
    (value: string) => {
      emitFilters({ maxDistanceKm: value });
    },
    [emitFilters]
  );

  const commitMaxDistanceKm = useCallback(
    (raw: string) => {
      const trimmed = raw.trim();
      if (trimmed === '') {
        setMaxDistanceKm('');
        setMaxDistanceError(null);
        if (filters.maxDistanceKm !== '') {
          emitMaxDistanceKmFilter('');
        }
        return;
      }

      if (!isNonNegativeDecimalWithMaxPlaces(trimmed, MAX_DISTANCE_KM_DECIMAL_PLACES)) {
        setMaxDistanceKm('');
        setMaxDistanceError(null);
        if (filters.maxDistanceKm !== '') {
          emitMaxDistanceKmFilter('');
        }
        return;
      }

      const num = Number(trimmed);
      if (num <= 0) {
        setMaxDistanceKm('');
        setMaxDistanceError(t('allocation.details.filters.maxDistanceKm.invalid'));
        if (filters.maxDistanceKm !== '') {
          emitMaxDistanceKmFilter('');
        }
        return;
      }

      const normalized = formatNumberToMaxPlaces(num, MAX_DISTANCE_KM_DECIMAL_PLACES);
      setMaxDistanceKm(normalized);
      setMaxDistanceError(null);
      if (normalized !== filters.maxDistanceKm) {
        emitMaxDistanceKmFilter(normalized);
      }
    },
    [emitMaxDistanceKmFilter, filters.maxDistanceKm, t]
  );

  const onMaxDistanceChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = normalizeDecimalInputToMaxPlaces(e.target.value, MAX_DISTANCE_KM_DECIMAL_PLACES);
    setMaxDistanceKm(raw);
    setMaxDistanceError(null);
  }, []);

  const onMaxDistanceBlur = useCallback(() => {
    commitMaxDistanceKm(maxDistanceKm);
  }, [commitMaxDistanceKm, maxDistanceKm]);

  const onMaxDistanceKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
    validatePositiveDecimalMaxPlacesInput(e, MAX_DISTANCE_KM_DECIMAL_PLACES);
    if (e.key !== 'Enter') {
      return;
    }
    e.preventDefault();
    (e.target as HTMLInputElement).blur();
  }, []);

  const onTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchText(value);
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      searchDebounceRef.current = setTimeout(() => {
        emitFilters({ searchText: value });
        searchDebounceRef.current = null;
      }, 100);
    },
    [emitFilters]
  );

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 1,
        width: '100%',
      }}
    >
      <Stack direction="column" spacing={2} sx={{ width: '100%' }}>
        <Stack
          direction="row"
          spacing={1.5}
          flexWrap="wrap"
          useFlexGap
          sx={{ alignItems: 'flex-end', width: '100%' }}
        >
          <TextField
            value={searchText}
            onChange={onTextChange}
            placeholder={t('allocation.details.filters.search')}
            size="small"
            sx={{ minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="material-symbols:search-rounded" />
                </InputAdornment>
              ),
            }}
          />
          <Autocomplete
            multiple
            options={competencyOptions}
            value={selectedCompetencies}
            getOptionLabel={(option) => option.label}
            onChange={(_, newValues) => {
              setSelectedCompetencies(newValues ?? []);
              emitFilters({
                selectedCompetencyIds: newValues?.map((competency) => competency.key) ?? [],
              });
            }}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.key}
                  label={option.label}
                  color="info"
                  variant="soft"
                  size="small"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('allocation.details.filters.competencies')}
                sx={{ minWidth: 220 }}
              />
            )}
            size="small"
          />
          <Autocomplete
            multiple
            options={businessUnitFilterOptions}
            value={selectedBusinessUnits}
            getOptionLabel={(option) => option.label}
            onChange={(_, newValues) => {
              setSelectedBusinessUnits(newValues ?? []);
              emitFilters({
                selectedBusinessUnitIds: newValues?.map((bu) => bu.key) ?? [],
              });
            }}
            disabled={businessUnitOptionsLoading}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.key}
                  label={option.label}
                  color="info"
                  variant="soft"
                  size="small"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('allocation.details.filters.businessUnits')}
                placeholder={
                  businessUnitOptionsLoading ? t('loading', { ns: 'common' }) : undefined
                }
                sx={{ minWidth: 220 }}
              />
            )}
            size="small"
          />
          <TextField
            value={durationDays}
            onChange={onDurationChange}
            onBlur={onDurationBlur}
            label={t('allocation.details.filters.durationDays.label')}
            placeholder={t('allocation.details.filters.durationDays.maxHint', {
              max: defaultDurationDays,
            })}
            size="small"
            sx={COMPACT_NUMERIC_FILTER_FIELD_SX}
            error={Boolean(durationError)}
            helperText={durationError ?? undefined}
            inputProps={{
              inputMode: 'decimal',
              pattern: getMaxDecimalPlacesPattern(MAX_DURATION_DECIMAL_PLACES),
            }}
            onKeyDown={onDurationKeyDown}
            InputProps={{
              endAdornment: <InputAdornment position="end">d</InputAdornment>,
            }}
          />
          {!isDeskService && (
            <Tooltip
              title={t('allocation.details.filters.maxDistanceKm.helper')}
              arrow
              placement="top"
            >
              <Box component="span" sx={{ ...COMPACT_NUMERIC_FILTER_FIELD_SX, display: 'inline-block' }}>
                <TextField
                  value={maxDistanceKm}
                  onChange={onMaxDistanceChange}
                  onBlur={onMaxDistanceBlur}
                  label={t('allocation.details.filters.maxDistanceKm.label')}
                  size="small"
                  fullWidth
                  error={Boolean(maxDistanceError)}
                  helperText={maxDistanceError ?? undefined}
                  inputProps={{
                    inputMode: 'decimal',
                    pattern: getMaxDecimalPlacesPattern(MAX_DISTANCE_KM_DECIMAL_PLACES),
                  }}
                  onKeyDown={onMaxDistanceKeyDown}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {t('allocation.details.filters.maxDistanceKm.unit')}
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Tooltip>
          )}
        </Stack>
        <Stack
          direction="row"
          spacing={1.5}
          flexWrap="wrap"
          useFlexGap
          sx={{ position: 'relative', alignItems: 'center', width: '100%' }}
        >
          <StyledButton
            ref={preferredDayButtonRef}
            variant="outlined"
            onClick={() => setIsDatePickerOpen(true)}
          >
            {filters.preferredDay
              ? dayjs(filters.preferredDay).format('DD/MM/YYYY')
              : t('allocation.details.filters.preferredDay')}
            {filters.preferredDay && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  emitFilters({ preferredDay: null });
                }}
                size="small"
              >
                <Iconify icon="material-symbols:close" />
              </IconButton>
            )}
          </StyledButton>
          <StyledButton
            variant="outlined"
            $isSelected={isOnlyInternal}
            onClick={() => {
              setIsOnlyInternal(!isOnlyInternal);
              emitFilters({ isOnlyInternal: !isOnlyInternal });
            }}
          >
            {t('allocation.details.filters.internalOnly')}
          </StyledButton>
          <StyledButton
            variant="outlined"
            $isSelected={isRecommended}
            onClick={() => {
              setIsRecommended(!isRecommended);
              emitFilters({ isRecommended: !isRecommended });
            }}
          >
            {t('allocation.details.filters.recommended')}
          </StyledButton>
          <StyledButton
            variant="outlined"
            $isSelected={isOnlyPreferred}
            onClick={() => {
              setIsOnlyPreferred(!isOnlyPreferred);
              emitFilters({ isOnlyPreferred: !isOnlyPreferred });
            }}
          >
            {t('allocation.details.filters.preferred')}
          </StyledButton>
          <Box
            sx={{
              position: 'absolute',
              width: 0,
              height: 0,
              overflow: 'visible',
              pointerEvents: 'none',
            }}
          >
            <RestrictedDatePicker
              dueDate={dayjs(activity?.dueDate)}
              earliestStart={dependencyEarliestStart ?? undefined}
              latestStart={dependencyLatestStart ?? undefined}
              label={t('allocation.details.filters.preferredDay')}
              value={filters.preferredDay}
              onChange={(newValue) => {
                emitFilters({ preferredDay: newValue });
                setIsDatePickerOpen(false);
              }}
              open={isDatePickerOpen}
              onClose={() => setIsDatePickerOpen(false)}
              slots={{
                field: () => null,
              }}
              slotProps={{
                popper: {
                  anchorEl: preferredDayButtonRef.current,
                  placement: 'bottom-start',
                  sx: (theme) => ({
                    pointerEvents: 'auto',
                    zIndex: theme.zIndex.modal + 2,
                  }),
                  modifiers: [{ name: 'preventOverflow', options: { padding: 8 } }],
                },
                desktopPaper: {
                  sx: { overflow: 'visible' },
                },
              }}
            />
          </Box>
          {showBreakInChangesToggle &&
            process.env.NEXT_PUBLIC_ENABLE_RESCHEDULE === 'true' &&
            onBreakInChangesSubmit && (
              <>
                <Switch
                  checked={breakInChangesEnabled}
                  onChange={(e) => onBreakInChangesChange(e.target.checked)}
                />
                <Typography variant="body2">
                  {t('allocation.details.filters.includeBreakInChanges')}
                </Typography>
              </>
            )}
        </Stack>
      </Stack>
    </Box>
  );
}
